// Upload core locations into Genius Zones. GeoJSON, KML, KMZ, a zipped shapefile, or a
// CSV of latitude/longitude. Only point locations are kept; each one is assigned to the
// zone polygon that contains it. Nothing is written to the server.

/* * The file picker on both core uploads (the preset and Pick Core Points). */
export const CORE_FILE_ACCEPT =
  ".geojson,.json,.kml,.kmz,.zip,.csv,.shp,.dbf,.prj,.shx,.cpg";
export const CORE_FILE_HINT =
  "GeoJSON, KML, KMZ, or a zipped shapefile of points. A CSV with latitude and longitude works too.";

/* * Zone-customize Upload Core Points: spatial files only. Points are kept by which zone contains them. */
export const ZONE_CORE_FILE_ACCEPT = ".geojson,.json,.kml,.kmz,.zip";
export const ZONE_CORE_FILE_HINT =
  "GeoJSON, KML, KMZ, or a zipped shapefile of points.";

import { autoMatchColumns, buildPointsFc, parseCsv } from "./csv";
import { layersFromDrop, type DropFile } from "./dropLayers";
import { clickInZone, type GeniusZone } from "./geniusCores";

export interface LngLat {
  lng: number;
  lat: number;
}

export interface ParsedCoreFile {
  points: LngLat[];
  /* * polygons, lines, and coordinates that are not longitude/latitude */
  skippedShapes: number;
  skippedRows: number;
  /* * a CSV was present and had no usable latitude and longitude columns */
  missingColumns: boolean;
  /* * a spatial file was present and nothing in it could be read */
  unreadable: boolean;
}

function usable(lng: number, lat: number): boolean {
  return (
    Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

/* * Point geometries only. A MultiPoint becomes one location per vertex. */
export function pointsFromFeatures(features: GeoJSON.Feature[]): {
  points: LngLat[];
  skippedShapes: number;
} {
  const points: LngLat[] = [];
  let skippedShapes = 0;
  for (const ft of features) {
    const g = ft?.geometry;
    if (!g) {
      skippedShapes++;
      continue;
    }
    if (g.type === "Point") {
      const [lng, lat] = g.coordinates;
      if (usable(lng, lat)) points.push({ lng, lat });
      else skippedShapes++;
    } else if (g.type === "MultiPoint") {
      let kept = 0;
      for (const c of g.coordinates) {
        const [lng, lat] = c;
        if (!usable(lng, lat)) continue;
        points.push({ lng, lat });
        kept++;
      }
      if (!kept) skippedShapes++;
    } else skippedShapes++;
  }
  return { points, skippedShapes };
}

export function pointsFromCsv(
  text: string,
  fileName = "the file",
): {
  points: LngLat[];
  skippedRows: number;
  missingColumns: boolean;
} {
  const parsed = parseCsv(text, fileName);
  const mapping = autoMatchColumns(parsed);
  if (mapping.lat == null || mapping.lon == null)
    return { points: [], skippedRows: 0, missingColumns: true };
  const built = buildPointsFc(parsed, mapping);
  const fc = built.fc as GeoJSON.FeatureCollection;
  const points: LngLat[] = [];
  for (const ft of fc.features) {
    const [lng, lat] = (ft.geometry as GeoJSON.Point).coordinates;
    if (usable(lng, lat)) points.push({ lng, lat });
  }
  return { points, skippedRows: built.skipped, missingColumns: false };
}

/* * First zone in walk order that contains the point. Points in no zone are counted and dropped. */
export function assignCoresToZones(
  zones: GeniusZone[],
  points: LngLat[],
): {
  placed: { zoneIndex: number; lng: number; lat: number }[];
  outside: number;
} {
  const placed: { zoneIndex: number; lng: number; lat: number }[] = [];
  let outside = 0;
  for (const p of points) {
    const zone = zones.find((z) => clickInZone(z, p.lng, p.lat));
    if (!zone) outside++;
    else placed.push({ zoneIndex: zone.index, lng: p.lng, lat: p.lat });
  }
  return { placed, outside };
}

export async function coreLocationsFromFiles(
  files: DropFile[],
): Promise<ParsedCoreFile> {
  const csvs = files.filter((f) => /\.csv$/i.test(f.name));
  const spatial = files.filter((f) => !/\.csv$/i.test(f.name));
  const points: LngLat[] = [];
  let skippedShapes = 0;
  let skippedRows = 0;
  let missingColumns = false;
  for (const f of csvs) {
    const text = await f.text();
    const parsed = pointsFromCsv(text, f.name);
    if (parsed.missingColumns) missingColumns = true;
    points.push(...parsed.points);
    skippedRows += parsed.skippedRows;
  }
  let spatialLayers = 0;
  if (spatial.length) {
    const layers = await layersFromDrop(spatial);
    spatialLayers = layers.length;
    for (const layer of layers) {
      const parsed = pointsFromFeatures(layer.features);
      points.push(...parsed.points);
      skippedShapes += parsed.skippedShapes;
    }
  }
  return {
    points,
    skippedShapes,
    skippedRows,
    missingColumns,
    unreadable:
      spatial.length > 0 && spatialLayers === 0 && points.length === 0,
  };
}

/* * Why an upload produced no cores, or null when there is something to place. */
export function coreUploadBlock(parsed: ParsedCoreFile): string | null {
  if (parsed.points.length) return null;
  if (parsed.missingColumns)
    return "That CSV needs latitude and longitude columns.";
  if (parsed.unreadable)
    return "Couldn’t read that file. Use GeoJSON, KML, KMZ, or a zipped shapefile.";
  if (parsed.skippedShapes)
    return "That file has no points. Upload a point GeoJSON, KML, KMZ, or zipped shapefile.";
  return "That file has no usable latitude and longitude.";
}
